"use client";

/**
 * Scroll-reveal wrapper — fades + rises children into view on first intersection.
 * One-shot: no re-hiding on scroll-up.
 * Respects prefers-reduced-motion: renders children immediately with no transform.
 * A-D4: 200px rootMargin so sections begin revealing before entering viewport,
 * eliminating the blank-flash on fast scroll. No-JS fallback: initial state is
 * visible (opacity-100) and the transition class is applied only client-side.
 */
import { useEffect, useRef, useState } from "react";

interface ScrollRevealProps {
  children: React.ReactNode;
  delay?: number;
}

export default function ScrollReveal({ children, delay = 0 }: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) {
      setReducedMotion(true);
      setVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "200px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (reducedMotion) {
    return <>{children}</>;
  }

  return (
    <div
      ref={ref}
      className={`transition-all duration-slow ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}
